import { registerPlugin } from '@capacitor/core'
import type { PendingOperation, SyncConflict } from '@/domain/types'
import { isNativePlatform } from '@/platform/platform'

interface NativeLocalStorePlugin {
  enqueueOperations(options: { operations: Array<Record<string, unknown>> }): Promise<void>
  removeOperations(options: { operationIds: string[] }): Promise<void>
  replaceOperations(options: { workspaceId: string; operations: Array<Record<string, unknown>> }): Promise<void>
  getPendingCount(options: { workspaceId: string }): Promise<{ count: number }>
  drainConflicts(): Promise<{ conflicts: Array<Record<string, unknown>> }>
}

const NativeLocalStore = registerPlugin<NativeLocalStorePlugin>('LocalStore')

function serializeOperation(operation: PendingOperation): Record<string, unknown> {
  return {
    operationId: operation.operationId,
    deviceId: operation.deviceId,
    workspaceId: operation.workspaceId,
    entityType: operation.entityType,
    entityId: operation.entityId,
    action: operation.action,
    baseVersion: operation.baseVersion,
    clientUpdatedAtMs: operation.clientUpdatedAtMs,
    payloadJson: JSON.stringify(operation.payload ?? {}),
    retryCount: operation.retryCount,
    nextRetryAtMs: operation.nextRetryAtMs,
    createdAtMs: operation.createdAtMs,
  }
}

// Mirror of the Dexie pendingOperations queue inside Room, so WorkManager can
// push offline changes without the WebView. All calls are best-effort no-ops on web.
export const localStoreMirror = {
  async enqueue(operations: PendingOperation[]) {
    if (!isNativePlatform() || !operations.length) return
    try {
      await NativeLocalStore.enqueueOperations({ operations: operations.map(serializeOperation) })
    } catch (error) {
      console.warn('LocalStore enqueue mirror failed', error)
    }
  },
  async remove(operationIds: string[]) {
    if (!isNativePlatform() || !operationIds.length) return
    try {
      await NativeLocalStore.removeOperations({ operationIds })
    } catch (error) {
      console.warn('LocalStore remove mirror failed', error)
    }
  },
  async replaceAll(workspaceId: string, operations: PendingOperation[]) {
    if (!isNativePlatform()) return
    try {
      await NativeLocalStore.replaceOperations({ workspaceId, operations: operations.map(serializeOperation) })
    } catch (error) {
      console.warn('LocalStore replace mirror failed', error)
    }
  },
  async drainConflicts(): Promise<SyncConflict[]> {
    if (!isNativePlatform()) return []
    try {
      const { conflicts } = await NativeLocalStore.drainConflicts()
      return conflicts.map(raw => ({
        id: String(raw.id),
        workspaceId: String(raw.workspaceId),
        entityType: raw.entityType as SyncConflict['entityType'],
        entityId: String(raw.entityId),
        operationId: String(raw.operationId),
        localPayload: JSON.parse(String(raw.localPayloadJson || '{}')) as Record<string, unknown>,
        serverEntity: JSON.parse(String(raw.serverEntityJson || '{}')) as Record<string, unknown>,
        detectedAtMs: Number(raw.detectedAtMs) || Date.now(),
        resolvedAtMs: null,
        resolution: null,
      }))
    } catch (error) {
      console.warn('LocalStore drainConflicts failed', error)
      return []
    }
  },
}
